"use client";

/**
 * Auth landing page — split layout, sign-in only.
 *
 * Left rail (lg+ only): brand panel with gradient backdrop. Hidden on mobile
 * so the form fills the viewport. Stays identical for whatever auth flow ends
 * up on the right.
 * Right rail: sign-in form, vertically centered.
 *
 * Registration is intentionally not surfaced in the UI right now. The
 * RegisterForm component still exists for when we open signup back up.
 */

import { Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { BrandMark } from "@/shared/components/BrandMark";
import { LoginForm } from "@/features/auth";

function AuthPage() {
  const router = useRouter();
  const searchParams = useSearchParams();

  function handleSuccess() {
    const returnTo = searchParams.get("from") || "/";
    router.push(returnTo);
  }

  return (
    <div className="grid min-h-screen w-full lg:grid-cols-2">
      <BrandPanel />
      <main className="flex flex-col items-center justify-center bg-background px-6 py-12 sm:px-10">
        <div className="w-full max-w-sm space-y-8">
          <header className="space-y-2">
            <div className="flex items-center gap-2 lg:hidden">
              <BrandMark />
              <span className="text-base font-semibold tracking-tight">HammerHead</span>
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">Welcome back</h1>
            <p className="text-sm text-muted-foreground">
              Sign in to pick up where you left off.
            </p>
          </header>

          <LoginForm onSuccess={handleSuccess} />

          <p className="text-center text-xs text-muted-foreground">
            By continuing you agree to the terms of service and privacy policy.
          </p>
        </div>
      </main>
    </div>
  );
}

function BrandPanel() {
  // Plain ground, no gradient.
  //
  // This panel carried a three-layer backdrop — two radial blooms and a
  // linear wash — plus a dot-grid overlay. It was violet-hardcoded, then
  // palette-derived, and it was still the wrong instrument: a marketing
  // backdrop on a screen whose only job is to take a password. The type
  // carries the brand; the surface should get out of its way.
  //
  // `bg-background` rather than a literal white so the panel still answers to
  // the face — paper in light, deep ground in dark — and the border is what
  // separates it from the form, which is the same rule the rest of the app
  // follows: lines carry structure.
  return (
    <aside
      aria-hidden="true"
      className="hidden border-r border-border bg-background lg:flex lg:flex-col lg:justify-between"
    >
      <header className="flex items-center gap-2 px-10 pt-10">
        <BrandMark />
        <span className="text-base font-semibold tracking-tight">HammerHead</span>
      </header>

      <div className="max-w-md space-y-4 px-10 pb-16">
        <p className="text-3xl font-semibold leading-tight tracking-tight">
          Capture every input. Surface every signal.
        </p>
        <p className="text-sm text-muted-foreground">
          Your second brain for notes, sessions, and the patterns you didn&apos;t
          know you were tracking — synthesized continuously by your own copilot.
        </p>
      </div>
    </aside>
  );
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="grid min-h-screen w-full lg:grid-cols-2">
          {/* the loading twin of BrandPanel — same ground, same border, so
              the fallback and the real panel cannot drift apart */}
          <div className="hidden border-r border-border bg-background lg:block" />
          <div className="flex items-center justify-center bg-background">
            <div className="animate-pulse text-sm text-muted-foreground">Loading…</div>
          </div>
        </div>
      }
    >
      <AuthPage />
    </Suspense>
  );
}
